// The collapsed left rail's own tooltip, and ONLY its own: it places the
// bubble beside the icon (the rail is vertical and hugs the edge of the
// screen), where the app-wide one in ../tooltip places it above. Everything
// else uses that one — see CLAUDE.md.
import { useCallback, useLayoutEffect, useRef, useState, type FocusEvent } from "react";
import { createPortal } from "react-dom";
import { computeTooltipPosition, type TooltipPosition } from "./compute_tooltip_position";

const TOOLTIP_CLASS = [
	"pointer-events-none fixed z-[10050] max-w-[220px] whitespace-nowrap",
	"rounded-xs px-[8px] py-[5px]",
	"bg-dark-base text-white dark:bg-white dark:text-dark-base",
	"font-['Inter'] text-[13px] font-medium leading-[140%]",
	"[box-shadow:0_4px_12px_rgba(0,0,0,0.18)]",
].join(" ");

type TooltipProps = {
	label: string;
	/** Skip the tooltip entirely — used for items that already show their label. */
	disabled?: boolean;
	children: React.ReactNode;
};

const Tooltip = ({ label, disabled = false, children }: TooltipProps) => {
	const anchorRef = useRef<HTMLDivElement | null>(null);
	const tooltipRef = useRef<HTMLDivElement | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [position, setPosition] = useState<TooltipPosition | null>(null);

	// No delay before showing. On the collapsed rail this tooltip is the only
	// thing naming an icon, and the 300ms wait it used to keep read as the label
	// simply not being there — the same complaint that took the reader off the
	// browser's own `title`.
	const open = useCallback(() => {
		if (!disabled) setIsOpen(true);
	}, [disabled]);

	const close = useCallback(() => {
		setIsOpen(false);
		setPosition(null);
	}, []);

	// Only keyboard focus earns the label. A clicked icon keeps focus, and the browser
	// hands it back on return to the tab or when a modal closes — with no pointer there.
	const openOnFocus = useCallback(
		(e: FocusEvent<HTMLDivElement>) => {
			try {
				if (!(e.target as HTMLElement).matches(":focus-visible")) return;
			} catch {
				// Older engines (and jsdom) may not know the selector.
			}
			open();
		},
		[open],
	);

	// Leaving the tab or window sends no mouseleave, so the label would outlive the visit.
	useLayoutEffect(() => {
		if (!isOpen) return;
		const closeIfHidden = () => {
			if (document.visibilityState === "hidden") close();
		};
		window.addEventListener("blur", close);
		document.addEventListener("visibilitychange", closeIfHidden);
		return () => {
			window.removeEventListener("blur", close);
			document.removeEventListener("visibilitychange", closeIfHidden);
		};
	}, [isOpen, close]);

	// Measure after the tooltip is in the DOM but before paint, so it never flashes
	// at the top-left corner before being positioned.
	useLayoutEffect(() => {
		if (!isOpen) return;
		const anchor = anchorRef.current?.getBoundingClientRect();
		const tooltip = tooltipRef.current?.getBoundingClientRect();
		if (!anchor || !tooltip) return;
		setPosition(
			computeTooltipPosition(
				anchor,
				{ width: tooltip.width, height: tooltip.height },
				{ width: window.innerWidth, height: window.innerHeight },
			),
		);
	}, [isOpen, label]);

	return (
		<div
			ref={anchorRef}
			className="relative flex"
			onMouseEnter={open}
			onMouseLeave={close}
			onMouseDown={close}
			onFocus={openOnFocus}
			onBlur={close}
		>
			{children}
			{isOpen &&
				createPortal(
					<div
						ref={tooltipRef}
						role="tooltip"
						className={TOOLTIP_CLASS}
						style={{
							top: position?.top ?? 0,
							left: position?.left ?? 0,
							visibility: position ? "visible" : "hidden",
						}}
					>
						{label}
					</div>,
					document.body,
				)}
		</div>
	);
};

export default Tooltip;
